import { apiClient } from '../utils/apiClient';
import type {
  AgentRun,
  AgentRunDetail,
  AgentRunStep,
  AgentApproval,
  AgentArtifact,
  AgentEmailDraft,
  InboundEmail,
  CompanyAgentSettings,
  AgentMailbox,
  PolicyDecision,
} from '../types/agent';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';
const qs = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const agentApi = {
  // Inbox
  inbox: (params: { status?: string; q?: string; from?: string; page?: number } = {}) =>
    apiClient.get<{ total: number; emails: InboundEmail[] }>(`/api/agent/inbox${qs(params)}`),
  inboxDetail: (id: string) =>
    apiClient.get<{ email: InboundEmail & { hasHtml: boolean }; runs: Array<Pick<AgentRun, 'id' | 'status' | 'summary' | 'createdAt' | 'mode'>>; documents: Array<{ id: string; name: string; customerId: string | null; projectId: string | null; isStaged: boolean; type: string }> }>(`/api/agent/inbox/${id}`),
  rerun: (id: string) => apiClient.post<{ runId: string }>(`/api/agent/inbox/${id}/rerun`),
  assign: (id: string, userId: string) => apiClient.post<{ runId: string }>(`/api/agent/inbox/${id}/assign`, { userId }),

  // Runs
  runs: (params: { status?: string; scope?: 'mine' | 'company'; customerId?: string; page?: number } = {}) =>
    apiClient.get<{ total: number; runs: AgentRun[] }>(`/api/agent/runs${qs(params)}`),
  run: (id: string) => apiClient.get<AgentRunDetail>(`/api/agent/runs/${id}`),
  steps: (id: string, after: number) => apiClient.get<{ status: string; steps: AgentRunStep[] }>(`/api/agent/runs/${id}/steps?after=${after}`),
  cancel: (id: string) => apiClient.post(`/api/agent/runs/${id}/cancel`),
  manualRun: (body: { customerId?: string | null; projectId?: string | null; instructions: string }) => apiClient.post<{ runId: string }>(`/api/agent/runs/manual`, body),

  // Approvals
  approvals: (status = 'pending') => apiClient.get<{ approvals: AgentApproval[] }>(`/api/agent/approvals?status=${status}`),
  approve: (id: string, note?: string) => apiClient.post<{ status: string; executionResult: any }>(`/api/agent/approvals/${id}/approve`, { note }),
  reject: (id: string, note?: string) => apiClient.post<{ status: string }>(`/api/agent/approvals/${id}/reject`, { note }),
  approvalPreview: (token: string) => apiClient.get<{ status: string; action: { displayName: string; tool: string }; reason: string | null; requestedAt: string; runId: string }>(`/api/agent/approvals/act?token=${encodeURIComponent(token)}`, { noAuth: true }),
  approvalAct: (token: string, decision: 'approve' | 'reject', note?: string) => apiClient.post<{ status: string; runId: string }>(`/api/agent/approvals/act`, { token, decision, note }, { noAuth: true }),

  // Drafts
  updateDraft: (id: string, body: Partial<Pick<AgentEmailDraft, 'subject' | 'bodyText' | 'toAddresses' | 'ccAddresses' | 'attachments'>>) => apiClient.put<{ draft: AgentEmailDraft }>(`/api/agent/drafts/${id}`, body),
  discardDraft: (id: string) => apiClient.post<{ draft: AgentEmailDraft }>(`/api/agent/drafts/${id}/discard`),

  // Artifacts
  artifacts: (params: { type?: string; customerId?: string; runId?: string; page?: number } = {}) =>
    apiClient.get<{ total: number; artifacts: AgentArtifact[] }>(`/api/agent/artifacts${qs(params)}`),

  // Settings / mailbox / aliases
  settings: () =>
    apiClient.get<{ settings: CompanyAgentSettings | null; policy: Array<{ tool: string; policy: PolicyDecision }>; defaultToolPolicy: Record<string, string>; runsToday: number; platformEnabled: boolean; canEdit: boolean }>(`/api/agent/settings`),
  saveSettings: (body: Partial<CompanyAgentSettings>) => apiClient.put<{ settings: CompanyAgentSettings }>(`/api/agent/settings`, body),
  mailboxes: () =>
    apiClient.get<{ mailboxes: AgentMailbox[]; platformEnabled: boolean; transport: string; maxMessageMb: number }>(`/api/agent/mailboxes`),
  aliases: () => apiClient.get<{ aliases: Array<{ id: string; email: string; verifiedAt: string | null; verificationSentAt: string | null }> }>(`/api/agent/aliases`),
  addAlias: (email: string) => apiClient.post(`/api/agent/aliases`, { email }),
  resendAlias: (id: string) => apiClient.post(`/api/agent/aliases/${id}/resend`),
  deleteAlias: (id: string) => apiClient.delete(`/api/agent/aliases/${id}`),
  verifyAlias: (token: string) => apiClient.post<{ verified: boolean; email: string; alreadyVerified?: boolean }>(`/api/agent/aliases/verify`, { token }, { noAuth: true }),

  notifications: (since?: string) =>
    apiClient.get<{ pendingApprovals: number; runs: Array<{ id: string; status: string; mode: string; finishedAt: string | null; updatedAt: string; subject: string | null; summary: string }> }>(`/api/agent/notifications${qs({ since })}`),

  simulate: (file: File, asUserId?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (asUserId) fd.append('asUserId', asUserId);
    return apiClient.post<{ outcome: string; reason: string | null; inboundEmailId: string | null; runId: string | null }>(`/api/agent/inbound/simulate`, fd);
  },
};

/**
 * Live run updates over SSE (fetch-streamed so the auth header is sent —
 * EventSource can't carry it). Returns an abort function.
 */
export function streamRun(
  runId: string,
  after: number,
  onEvent: (event: { type: 'step'; step: AgentRunStep } | { type: 'run'; run: Partial<AgentRun> } | { type: 'paused' } | { type: 'end' }) => void
): () => void {
  const controller = new AbortController();
  const token = localStorage.getItem('riskninja-token');
  (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/agent/runs/${runId}/stream?after=${after}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal: controller.signal,
      });
      if (!res.ok || !res.body) return;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        for (const chunk of chunks) {
          const line = chunk.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          try {
            onEvent(JSON.parse(line.slice(6)));
          } catch {
            /* ignore malformed */
          }
        }
      }
    } catch {
      /* aborted or network — caller falls back to polling */
    }
  })();
  return () => controller.abort();
}

export interface AcordAiField {
  name: string;
  label: string;
  section: string;
  type: string;
  options?: string[];
  value: string;
  source: 'ai' | 'user';
  documentId?: string | null;
  documentName?: string | null;
  quote?: string | null;
  confidence?: number | null;
}

export interface AcordAiFillResult {
  fields: AcordAiField[];
  blank: Array<{ name: string; label: string; section: string; type: string; options?: string[] }>;
  documentsUsed: Array<{ id: string; name: string }>;
  documentsSkipped: string[];
}

export type AcordRawFieldInput = Pick<AcordAiField, 'value' | 'source' | 'documentId' | 'documentName' | 'quote' | 'confidence'>;

export const acordApi = {
  list: () => apiClient.get<{ forms: Array<{ id: string; formNumber: string; editionDate: string; title: string; category: string; description: string | null; mappingStatus: string; pageCount: number; version: number; semanticKeys: string[] }> }>(`/api/acord-forms`),
  previewUrl: (id: string) => `${API_BASE_URL}/api/acord-forms/${id}/preview`,
  assemble: (id: string, body: { customerId: string; projectId?: string; holderId?: string | null; holder?: any; descriptionOfOperations?: string }) =>
    apiClient.post<{ values: Record<string, string>; sources: Record<string, { source: string; documentName?: string; detail?: string }>; unresolvedKeys: string[]; formKeys: string[]; labels: Record<string, { label: string; group: string; kind: string }>; mappingStatus: string }>(`/api/acord-forms/${id}/assemble`, body),
  aiFill: (id: string, body: { customerId: string; projectId?: string; values?: Record<string, string>; instructions?: string }) =>
    apiClient.post<AcordAiFillResult>(`/api/acord-forms/${id}/ai-fill`, body),
  fill: (id: string, body: { customerId: string; projectId: string; values: Record<string, string>; flatten?: boolean; title?: string; holderId?: string | null; holder?: any; descriptionOfOperations?: string; rawFields?: Record<string, AcordRawFieldInput>; aiFill?: boolean; instructions?: string }) =>
    apiClient.post<{ fillId: string; documentId: string; documentName: string; flattenedDocumentId: string | null; unresolvedKeys: string[]; filledKeys: string[]; skipped: Array<{ key: string; field: string; reason: string }>; aiFilledCount: number; blankFields: Array<{ name: string; label: string; section: string }> }>(`/api/acord-forms/${id}/fill`, body),
  fills: (customerId: string) => apiClient.get<{ fills: any[] }>(`/api/acord-forms/fills?customerId=${customerId}`),
  coi: (body: { customerId: string; projectId: string; holderId?: string | null; holder?: any; descriptionOfOperations?: string }) =>
    apiClient.post<{ documentId: string; documentName: string; flattenedDocumentId: string | null; unresolvedKeys: string[]; holderName: string | null }>(`/api/acord-forms/certificates/coi`, body),
  coe: (body: { customerId: string; projectId: string; documentIds: string[] }) => apiClient.post<{ documentId: string; documentName: string }>(`/api/acord-forms/certificates/coe`, body),

  // sysadmin
  adminList: () => apiClient.get<{ forms: any[] }>(`/api/acord-forms/admin`),
  adminGet: (id: string) => apiClient.get<{ form: any; fillCount: number; versions: any[] }>(`/api/acord-forms/admin/${id}`),
  adminUpload: (fd: FormData) => apiClient.post<{ form: any }>(`/api/acord-forms/admin`, fd),
  adminUpdate: (id: string, body: Record<string, any>) => apiClient.put<{ form: any }>(`/api/acord-forms/admin/${id}`, body),
  adminPageUrl: (id: string, page: number) => `${API_BASE_URL}/api/acord-forms/admin/${id}/page/${page}`,
  adminDownloadUrl: (id: string) => `${API_BASE_URL}/api/acord-forms/admin/${id}/download`,
  semanticKeys: () => apiClient.get<{ keys: Array<{ key: string; label: string; group: string; kind?: string; hint?: string }> }>(`/api/acord-forms/admin/semantic-keys`),
  labelFields: (id: string) => apiClient.post<{ labeled: number; excluded: number; total: number; form: any }>(`/api/acord-forms/admin/${id}/label-fields`),
  proposeMapping: (id: string) => apiClient.post<{ fieldMap: Record<string, any>; warnings: string[]; confidence: Record<string, number> }>(`/api/acord-forms/admin/${id}/propose-mapping`),
  saveMapping: (id: string, fieldMap: Record<string, any>, mappingStatus?: string) => apiClient.put<{ form: any; warnings: string[] }>(`/api/acord-forms/admin/${id}/mapping`, { fieldMap, mappingStatus }),
  testFillUrl: (id: string) => `${API_BASE_URL}/api/acord-forms/admin/${id}/test-fill`,
  license: () => apiClient.get<{ licenseeName: string; licenseNumber: string; footerText: string; licenseTerms: string }>(`/api/acord-forms/admin/license`),
  saveLicense: (body: Record<string, string>) => apiClient.put(`/api/acord-forms/admin/license`, body),
};

export const holdersApi = {
  list: (customerId: string) => apiClient.get<{ holders: any[] }>(`/api/certificate-holders/customer/${customerId}`),
  create: (customerId: string, body: Record<string, any>) => apiClient.post<{ holder: any }>(`/api/certificate-holders/customer/${customerId}`, body),
  update: (id: string, body: Record<string, any>) => apiClient.put<{ holder: any }>(`/api/certificate-holders/${id}`, body),
  remove: (id: string) => apiClient.delete(`/api/certificate-holders/${id}`),
};

/** Authenticated binary fetch → object URL (images/PDFs behind auth). */
export async function fetchBlobUrl(url: string, init?: RequestInit): Promise<string> {
  const token = localStorage.getItem('riskninja-token');
  const res = await fetch(url, { ...init, headers: { ...(init?.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      msg = (await res.json()).error || msg;
    } catch {
      /* binary */
    }
    throw new Error(msg);
  }
  return URL.createObjectURL(await res.blob());
}

/**
 * Open an authenticated file in a new tab. The tab is opened synchronously
 * inside the click (popup blockers drop window.open after an await) and
 * pointed at the blob once it arrives.
 */
export async function openAuthedFileInNewTab(url: string): Promise<void> {
  const tab = window.open('', '_blank');
  try {
    const blobUrl = await fetchBlobUrl(url);
    if (tab) tab.location.href = blobUrl;
    else window.location.assign(blobUrl);
  } catch (err) {
    tab?.close();
    throw err;
  }
}

export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as any).message === 'string') return (err as any).message;
  return fallback;
}
